const ALLOWED_QUERY_PARAMETERS = new Set([
  "utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_content", "utm_term",
  "ad_id", "adset_id", "campaign_id", "fbclid", "gclid", "gbraid", "wbraid",
  "ttclid", "msclkid", "checkout_cancelled", "locale",
]);
const SECRET_PROPERTY = /^(?:token_hash|access_token|refresh_token|id_token|authorization|password|client_secret|signature|x-amz-(?:signature|credential|security-token))$/i;

/** Only retain bounded attribution values; credentials and fragments never enter telemetry. */
export function sanitizeAnalyticsUrl(value: string): string {
  const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value);
  const isProtocolRelative = value.startsWith("//");
  try {
    const url = new URL(value, "https://analytics.invalid");
    if (!["http:", "https:"].includes(url.protocol)) return "[redacted URL]";
    url.username = "";
    url.password = "";
    url.hash = "";
    // Invoice share grants are bearer credentials in the path, not the query.
    url.pathname = url.pathname.replace(/(\/(?:invoice|invoice-drafts)\/)[^/]+/g, "$1[redacted]");
    const clean = new URLSearchParams();
    url.searchParams.forEach((entry, key) => {
      if (ALLOWED_QUERY_PARAMETERS.has(key.toLowerCase()) && entry.length <= 300 &&
        !/[\r\n]/.test(entry) && !/https?:|[?&](?:token|email|code)=/i.test(entry)) {
        clean.append(key, entry);
      }
    });
    url.search = clean.toString();
    if (isAbsolute) return url.toString();
    if (isProtocolRelative) return "//" + url.host + url.pathname + url.search;
    return url.pathname + url.search;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
}

// href="…" and attr__href="…" in posthog-js's $elements_chain (quotes escaped as \").
// An attribute name starts the chain or follows ":", ";" or the previous value's quote.
const ELEMENTS_CHAIN_HREF = /((?:attr__)?href)="((?:\\.|[^"\\])*)"/g;

function sanitizeText(value: string, key: string): string {
  // Rage and dead clicks copy the nearest link's href, which can be relative,
  // mailto: or tel:, into $elements and $elements_chain.
  if (/^(?:attr__)?href$/i.test(key)) return sanitizeAnalyticsUrl(value);
  if (key === "$elements_chain") {
    value = value.replace(ELEMENTS_CHAIN_HREF, (match: string, name: string, href: string, offset: number) =>
      offset > 0 && !':";'.includes(value[offset - 1]) ? match
        : `${name}="${sanitizeAnalyticsUrl(href.replace(/\\"/g, '"')).replace(/"/g, '\\"')}"`);
  }
  if (/^(?:https?:\/\/|\/\/|\/)/i.test(value)) return sanitizeAnalyticsUrl(value);
  if (/(?:url|href|referrer|filename)$/i.test(key) && /[?#]/.test(value)) {
    return sanitizeAnalyticsUrl(value);
  }
  return value.replace(/https?:\/\/[^\s"'<>\])}]+/gi, (url) => sanitizeAnalyticsUrl(url));
}

/** Covers event properties, nested exceptions/links, and initial person URL properties. */
export function sanitizePostHogProperties(
  properties: Record<string, unknown>,
  allowProjectToken = false,
): Record<string, unknown> {
  const seen = new WeakMap<object, unknown>();
  const visit = (value: unknown, key: string, depth: number): unknown => {
    if (SECRET_PROPERTY.test(key) || (key.toLowerCase() === "token" &&
      (!allowProjectToken || depth > 1 || value !== process.env.NEXT_PUBLIC_POSTHOG_KEY))) return "[redacted]";
    if (typeof value === "string") return sanitizeText(value, key);
    if (value === null || typeof value !== "object" || value instanceof Date) return value;
    if (depth > 20) return "[redacted nested value]";
    if (seen.has(value)) return "[redacted circular value]";
    if (Array.isArray(value)) {
      const array: unknown[] = [];
      seen.set(value, array);
      value.forEach((item) => array.push(visit(item, key, depth + 1)));
      return array.every((item, index) => item === value[index]) ? value : array;
    }
    const record: Record<string, unknown> = {};
    seen.set(value, record);
    Object.entries(value).forEach(([name, item]) => {
      record[name] = visit(item, name, depth + 1);
    });
    return Object.keys(record).every((name) => record[name] === (value as Record<string, unknown>)[name])
      ? value : record;
  };
  return visit(properties, "", 0) as Record<string, unknown>;
}
